import { ConsolidatedLocationView, UnitGpsDto } from '@mark43/cad-api';
import { BlankModal, cssVar, Flex, HStack, Icon, ModalBody, Spinner, Text } from '@cad-arc';
import { observer } from 'mobx-react-lite';
import { useEffect, useState } from 'react';
import { getClosestLocation } from '../common/helpers/get-closest-location';
import { useLocationApi } from '../location-select/hooks/use-location-api';
import { isValidLatLng } from '../location/helpers';
import { useFullscreenModalControls } from '../modals/context/modal-context-provider';
import { useUnitLocationStore } from '../fr-unit-location/use-unit-location-store';

function DisplayText({ text }: { text: string }) {
    return (
        <Text fontSize="3xl" style={{ wordBreak: 'break-word' }}>
            {text}
        </Text>
    );
}

export type FrUnitShowCurrentLocationModalComponentType = typeof FrUnitShowCurrentLocationModal;

export const FrUnitShowCurrentLocationModal = observer(
    function FrUnitShowCurrentLocationModal(): React.ReactElement {
        const modalControls = useFullscreenModalControls();
        const { searchLocations, isLoadingResults, errorLoadingLocationData } = useLocationApi();
        const unitLocationStore = useUnitLocationStore();

        const [
            closestReverseGeocodeLocation,
            setClosestReverseGeocodeLocation,
        ] = useState<ConsolidatedLocationView>();

        const { lat: unitGpsLatitude, lng: unitGpsLongitude } =
            unitLocationStore?.unitGpsInfo || {};

        const displayLineFirst = closestReverseGeocodeLocation?.displayLineFirst;
        const displayLineLast = closestReverseGeocodeLocation?.displayLineLast;

        const showLocationNotFoundText =
            !isLoadingResults &&
            !errorLoadingLocationData &&
            (!closestReverseGeocodeLocation || !displayLineFirst || !displayLineLast);

        useEffect(
            function searchUsersCurrentReverseGeocodeLocation() {
                if (!isValidLatLng(unitGpsLatitude, unitGpsLongitude)) {
                    return;
                }

                const queryString = `${unitGpsLatitude},${unitGpsLongitude}`;

                setClosestReverseGeocodeLocation(undefined);

                searchLocations(queryString)
                    .then((results) => {
                        const closestLocation = getClosestLocation(results);

                        if (closestLocation) {
                            setClosestReverseGeocodeLocation(closestLocation);
                        }
                    })
                    .catch(() => {
                        setClosestReverseGeocodeLocation(undefined);
                    });
            },
            [searchLocations, unitGpsLatitude, unitLocationStore, unitGpsLongitude]
        );

        useEffect(
            function resetUnitGpsLocationData() {
                if (errorLoadingLocationData) {
                    unitLocationStore?.setUnitGpsLocation({} as UnitGpsDto);
                }
            },
            [errorLoadingLocationData, unitLocationStore]
        );

        return (
            <BlankModal
                isOpen
                hideCloseButton
                isCentered
                size="md"
                style={{
                    minHeight: 185,
                    maxHeight: 280,
                    overflow: 'auto',
                    background: 'var(--arc-colors-surface-foreground)',
                }}
                onClose={() => modalControls.close()}
            >
                <ModalBody
                    style={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                    }}
                >
                    {isLoadingResults && (
                        <HStack justifyContent="center">
                            <Spinner />
                        </HStack>
                    )}
                    {!isLoadingResults && errorLoadingLocationData && (
                        <DisplayText text="Error: Location request failed" />
                    )}
                    {showLocationNotFoundText && <DisplayText text="Location not found" />}
                    {displayLineFirst && displayLineLast && (
                        <Flex gap={2}>
                            <Flex alignItems="center">
                                <Icon
                                    aria-label="Location pin icon"
                                    icon="Location"
                                    color={cssVar('arc.colors.text.primary')}
                                    size="lg"
                                />
                            </Flex>
                            <DisplayText text={`${displayLineFirst} ${displayLineLast}`} />
                        </Flex>
                    )}
                </ModalBody>
            </BlankModal>
        );
    }
);
